import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ObjectException } from 'global/errors';
import moment from 'moment';

import { useFreecodeContextRun, useFreecodeContextData } from 'hooks/useContext';
import { isNotValidObjectID } from 'functions/stringValidation';
import services from 'api/services';
import useFreecodeLivePrimus from 'components/FreecodePage/useFreecodeLivePrimus';

const { ConfigService, FreecodeService, TicketService } = services;

export default function useTestLoadFunction(ticketId) {
  const { isInitialized } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(undefined);

  const { setTest, setTestImpl, setLanguageData, setInput, setTicket } = useFreecodeContextData();
  const { setLanguage, setCodeSol } = useFreecodeContextRun();

  const sendTicketPrimusRoomRequest = useFreecodeLivePrimus();

  useEffect(() => {
    if (!isInitialized || !ticketId) return;
    const fetchData = async () => {
      try {
        if (isNotValidObjectID(ticketId)) throw new ObjectException(`Not a valid id ${ticketId}`, 'VALIDATION_ERROR');

        // Get language versions before ticket data so ticket does not start by mistake if languages fail
        const languageVersions = await ConfigService.getLanguageVersions();
        if (!languageVersions?.value?.[4]?.python)
          throw new ObjectException('Failed to fetch language versions', 'VALIDATION_ERROR');

        const response = await FreecodeService.loadFreecodeTakeTicket(ticketId);
        const { test, testImpl, ticket, input } = response;

        if (!test?._id) throw new ObjectException('Test missing from response', 'VALIDATION_ERROR');
        if (!testImpl?._id) throw new ObjectException('Implementation missing from response', 'VALIDATION_ERROR');
        if (test?._id !== testImpl?.test)
          throw new ObjectException('Test and implementation misssmatch', 'VALIDATION_ERROR');
        if (ticket?.test !== test._id) throw new ObjectException('Test and ticket misssmatch', 'VALIDATION_ERROR');

        let allowedLanguages = languageVersions?.value?.[testImpl?.executionVersion];
        if (!allowedLanguages?.python) throw new ObjectException('Language versions missing', 'VALIDATION_ERROR');
        if (ticket.languagesAllowed?.length) {
          allowedLanguages = {};
          for (const language of ticket.languagesAllowed) {
            allowedLanguages[language] = languageVersions.value[testImpl.executionVersion][language];
          }
        }
        const foundLanguage =
          input?.language || (allowedLanguages['python'] ? 'python' : Object.keys(allowedLanguages)[0]);

        if (ticket.serverTime) {
          const diffToServerTimeInSeconds = moment(new Date()).diff(ticket.serverTime, 'seconds');
          ticket.diffToServerTimeInSeconds = diffToServerTimeInSeconds;
        }

        setInput(input);
        setTest(test);
        setTestImpl(testImpl);
        setLanguageData(allowedLanguages);
        setLanguage(foundLanguage);
        setCodeSol(input?.code || testImpl.generated?.skeletons?.[foundLanguage] || 'Code not found...');
        setTicket(ticket);

        setLoading(false);
      } catch (err) {
        setError(err);
      }
    };
    fetchData();
  }, [isInitialized, setCodeSol, setInput, setLanguage, setLanguageData, setTest, setTestImpl, setTicket, ticketId]);

  useEffect(() => {
    if (loading || !ticketId) return;
    sendTicketPrimusRoomRequest({ operation: 'joinTicketRoom' });
  }, [loading, sendTicketPrimusRoomRequest, ticketId]);

  useEffect(() => {
    if (loading || !ticketId) return;
    try {
      TicketService.updateAwayTime(ticketId, { event: 'focus' });
    } catch (error) {
      console.error('Update away time error:', error);
    }
  }, [loading, ticketId]);

  return { loading, error };
}
